import { useEffect, useState } from "react";
import { Check, Copy, Loader2, Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import {
  usePaidMediaPromptMutations,
  usePaidMediaPrompts,
} from "@/hooks/use-paid-media-prompts";

import { cn } from "@/lib/utils";

import type { SavedPrompt } from "@/schema/brand-os/paid-media-prompts";

interface InsightsPromptDrawerProps {
  userId: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Anchor to attach the popover to. Typically the "Saved" button inside
   * the composer — we render `children` *inside* `PopoverTrigger` so the
   * button's positioning is what Radix uses to place the popover.
   */
  children: React.ReactNode;
  /**
   * Called when the user picks a row. The composer should populate its
   * textarea with `prompt.prompt` (the full text) and close the drawer.
   * No /run is fired here — the user can tweak before submitting.
   */
  onPick: (prompt: SavedPrompt) => void;
}

/**
 * Saved-prompts drawer.
 *
 * UX:
 *   - Each row shows the server-computed `display_label` plus a 2-line
 *     preview of the full prompt text so users can scan / copy without
 *     opening anything.
 *   - Click the row body → fires `onPick(prompt)`; the composer fills
 *     its textarea with `prompt.prompt` and the drawer closes.
 *   - Hover a row → copy + delete icons appear on the right. Copy puts
 *     the full prompt text on the clipboard; delete asks for confirm.
 *   - There is no "Edit" affordance — to "edit" a saved prompt the user
 *     just picks it, modifies in the textarea, and re-runs. The backend
 *     auto-saves the new wording as a separate dedup'd row, and the
 *     user can delete the old one if they want to clean up.
 */
export function InsightsPromptDrawer({
  userId,
  open,
  onOpenChange,
  children,
  onPick,
}: InsightsPromptDrawerProps) {
  const { prompts, isLoading } = usePaidMediaPrompts(userId);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(420px,calc(100vw-2rem))] p-0"
      >
        <header className="flex items-center justify-between border-b px-3 py-2">
          <div>
            <h4 className="text-xs font-semibold text-foreground">Saved prompts</h4>
            <p className="text-[11px] text-muted-foreground">
              Auto-saved when you run a new prompt
            </p>
          </div>
        </header>

        <div className="max-h-[420px] overflow-y-auto p-1.5">
          {isLoading ? (
            <SavedPromptListSkeleton />
          ) : prompts.length === 0 ? (
            <EmptyState />
          ) : (
            <ul className="space-y-0.5">
              {prompts.map((p) => (
                <SavedPromptRow
                  key={p.prompt_id}
                  prompt={p}
                  userId={userId}
                  onPick={(picked) => {
                    onPick(picked);
                    onOpenChange(false);
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------- row ---------------------------------------------------------

interface SavedPromptRowProps {
  prompt: SavedPrompt;
  userId: string | null | undefined;
  onPick: (prompt: SavedPrompt) => void;
}

function SavedPromptRow({ prompt, userId, onPick }: SavedPromptRowProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { deleteMut, isDeleting } = usePaidMediaPromptMutations(userId, {
    onDeleted: () => setConfirmOpen(false),
  });

  // We compare the trimmed prompt to the trimmed label to avoid showing
  // the same string twice when the prompt is short (display_label is
  // server-computed but for short prompts it equals the prompt itself).
  const showPreview = prompt.prompt.trim() !== prompt.display_label.trim();

  return (
    <li className="group relative">
      <button
        type="button"
        onClick={() => onPick(prompt)}
        className={cn(
          "block w-full rounded-md px-2 py-1.5 text-left transition-colors",
          // Right padding leaves room for the action icons so they don't
          // overlap the text on long single-line labels.
          "pr-16",
          "hover:bg-muted/60 focus:bg-muted/60 focus:outline-none",
        )}
        title="Click to use this prompt"
      >
        <p className="truncate text-xs font-medium text-foreground">{prompt.display_label}</p>
        {showPreview && (
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground">
            {prompt.prompt}
          </p>
        )}
      </button>

      {/* Action icons — overlay rather than inline so they can fade in
          on hover without reflowing the row. Positioned absolutely on
          the right edge of the row. */}
      <div
        className={cn(
          "absolute right-1.5 top-1.5 flex items-center gap-0.5",
          "opacity-0 transition-opacity",
          "group-hover:opacity-100 group-focus-within:opacity-100",
        )}
      >
        <CopyButton text={prompt.prompt} />
        <button
          type="button"
          aria-label={`Delete saved prompt: ${prompt.display_label}`}
          onClick={(e) => {
            e.stopPropagation();
            setConfirmOpen(true);
          }}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this saved prompt?</AlertDialogTitle>
            <AlertDialogDescription>
              "{prompt.display_label}" will be removed from your drawer. This
              can't be undone — but you can always re-run the prompt and it'll
              auto-save again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                deleteMut(prompt.prompt_id);
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="size-3.5 animate-spin" /> : null}
              <span className="ml-1.5">Delete</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </li>
  );
}

// ---------- copy button -------------------------------------------------

/**
 * Inline copy-to-clipboard with a brief checkmark confirmation. We use
 * the modern `navigator.clipboard.writeText` API and silently no-op on
 * older browsers / non-secure contexts (rare in this app, but worth
 * not crashing the row over).
 *
 * The button stops click propagation so clicking the icon doesn't also
 * fire the row's "use this prompt" handler.
 */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(t);
  }, [copied]);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Older / insecure-context fallback. No toast surface here, so we
      // just leave `copied` false — the icon won't flip and the user
      // will know the action didn't take.
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Copy prompt to clipboard"
          onClick={handleCopy}
          className={cn(
            "rounded p-1 transition-colors",
            copied
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      </TooltipTrigger>
      <TooltipContent>{copied ? "Copied" : "Copy prompt"}</TooltipContent>
    </Tooltip>
  );
}

// ---------- placeholders ------------------------------------------------

function EmptyState() {
  return (
    <div className="px-3 py-6 text-center">
      <p className="text-xs text-muted-foreground">No saved prompts yet.</p>
      <p className="mt-1 text-[11px] text-muted-foreground/80">
        Run a prompt and it'll show up here automatically.
      </p>
    </div>
  );
}

function SavedPromptListSkeleton() {
  return (
    <ul className="space-y-1 p-1">
      {Array.from({ length: 3 }, (_, i) => (
        <li key={i} className="space-y-1 px-2 py-1.5">
          <Skeleton className="h-3.5 w-4/5" />
          <Skeleton className="h-2.5 w-2/3" />
        </li>
      ))}
    </ul>
  );
}
